import { AutoComplete, Spin, Tag, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGeocodeSuggest } from '@/hooks/useGeocodeSuggest';
import { useIndustrialRefBookSuggest } from '@/hooks/useIndustrialRefBookSuggest';
import {
  INDUSTRIAL_REF_BOOK_COLOR_AGRO_POINT,
  INDUSTRIAL_REF_BOOK_COLOR_LOCATION,
  INDUSTRIAL_REF_BOOK_COLOR_TERMINAL,
} from '@/components/map/industrialRefBookLayer';
import type { GeocodeSuggestion } from '@/types/geocode';
import type { IndustrialRefBookSuggestion } from '@/types/industrialRefBook';

interface AddressAutoCompleteProps {
  readonly value: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  readonly onChange: (text: string) => void;
  readonly onSelect: (suggestion: GeocodeSuggestion) => void;
}

interface PickedOption {
  readonly value: string;
  readonly label: React.ReactNode;
  readonly suggestion: GeocodeSuggestion;
}

interface OptionGroup {
  readonly label: React.ReactNode;
  readonly options: readonly PickedOption[];
}

export default function AddressAutoComplete({
  value,
  placeholder,
  disabled,
  autoFocus,
  onChange,
  onSelect,
}: AddressAutoCompleteProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(value);
  const { suggestions: addressSuggestions, loading: loadingAddresses }
    = useGeocodeSuggest(query);
  const { items: industrialItems, loading: loadingIndustrial }
    = useIndustrialRefBookSuggest(query);

  const groups = useMemo<readonly OptionGroup[]>(() => {
    const out: OptionGroup[] = [];
    // Industrial-reference candidates take the top section. They carry
    // exact coordinates straight from the database and don't require a
    // geocoding round-trip when picked.
    if (industrialItems.length > 0) {
      out.push({
        label: (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('industrialRefBook.autocomplete.section')}
          </Typography.Text>
        ),
        options: industrialItems.map(_industrialOption),
      });
    }
    const filteredAddresses = addressSuggestions.filter(
      (s) => s.lat !== null && s.lon !== null,
    );
    if (filteredAddresses.length > 0) {
      out.push({
        label: (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('industrialRefBook.autocomplete.addressSection')}
          </Typography.Text>
        ),
        options: filteredAddresses.map(_addressOption),
      });
    }
    return out;
  }, [industrialItems, addressSuggestions, t]);

  const handleSearch = (text: string) => {
    setQuery(text);
    onChange(text);
  };

  const handleSelect = (_selectedValue: string, option: PickedOption) => {
    const picked = option.suggestion;
    onChange(picked.label);
    setQuery(picked.label);
    onSelect(picked);
  };

  const loading = loadingAddresses || loadingIndustrial;

  // AntD's AutoComplete grouped-options shape doesn't fit the
  // single-option generic — cast to its broader OptionType (label +
  // options or label + value) to avoid fighting the type parameter.
  return (
    <AutoComplete
      value={value}
      options={groups as unknown as DefaultOptionType[]}
      onChange={handleSearch}
      onSelect={(val, option) => handleSelect(val as string, option as PickedOption)}
      disabled={disabled}
      autoFocus={autoFocus}
      placeholder={placeholder ?? t('routes.form.addressPlaceholder')}
      notFoundContent={loading ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      filterOption={false}
    />
  );
}

function _industrialOption(item: IndustrialRefBookSuggestion): PickedOption {
  // The displayed label combines a coloured type tag with the title and
  // address; the value uses the id to guarantee uniqueness across
  // industrial / address sections.
  return {
    value: `irb:${item.id}`,
    suggestion: {
      label: item.short_title ?? item.title,
      lat: item.latitude,
      lon: item.longitude,
      source_id: `irb:${item.id}`,
    },
    label: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Tag color={_typeColor(item.type)} style={{ margin: 0, fontSize: 10 }}>
            {item.type}
          </Tag>
          <Typography.Text style={{ fontSize: 13 }} ellipsis>
            {item.title}
          </Typography.Text>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 11 }} ellipsis>
          {item.address}
        </Typography.Text>
      </div>
    ),
  };
}

function _addressOption(s: GeocodeSuggestion): PickedOption {
  return {
    value: `addr:${s.label}:${s.source_id ?? ''}`,
    suggestion: s,
    label: (
      <Typography.Text style={{ fontSize: 13 }} ellipsis>
        {s.label}
      </Typography.Text>
    ),
  };
}

function _typeColor(type: string): string | undefined {
  switch (type) {
    case 'LOCATION':
      return INDUSTRIAL_REF_BOOK_COLOR_LOCATION;
    case 'AGRO_POINT':
      return INDUSTRIAL_REF_BOOK_COLOR_AGRO_POINT;
    case 'TERMINAL':
      return INDUSTRIAL_REF_BOOK_COLOR_TERMINAL;
    default:
      return undefined;
  }
}
